import { CollectionViewer } from "@components/collection-viewer";
import { MsaViewer, type MsaViewerTab } from "@components/msa-viewer";
import {
  SequenceViewer,
  type SequenceViewerTab,
} from "@components/sequence-viewer";
import { TreeViewer } from "@components/tree-viewer";
import { useBlastStore } from "@fetch/blast/store";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { useViewerFile, useViewerSequences } from "./-viewerFileContext";

export const Route = createFileRoute("/_data/view/$fileId/")({
  component: ViewIndex,
});

function ViewIndex() {
  const { fileId } = Route.useParams();
  const file = useViewerFile();
  const sequences = useViewerSequences();
  const navigate = useNavigate();
  const setOnSearchReady = useBlastStore((state) => state.setOnSearchReady);

  const handleSearchReady = useCallback(() => {
    navigate({
      to: "/view/$fileId/searches",
      params: { fileId: file.id },
    });
  }, [navigate, file.id]);

  useEffect(() => {
    if (file.type === "GnomuxSequence") {
      setOnSearchReady(handleSearchReady);
      return () => setOnSearchReady(null);
    }
  }, [file.type, setOnSearchReady, handleSearchReady]);

  const handleSequenceTabChange = useCallback(
    (tab: SequenceViewerTab) => {
      if (tab === "features") {
        navigate({
          to: "/view/$fileId/features",
          params: { fileId: file.id },
        });
      } else if (tab === "primers") {
        navigate({ to: "/view/$fileId/primers", params: { fileId: file.id } });
      } else if (tab === "history") {
        navigate({ to: "/view/$fileId/history", params: { fileId: file.id } });
      } else if (tab === "searches") {
        navigate({
          to: "/view/$fileId/searches",
          params: { fileId: file.id },
        });
      }
    },
    [navigate, file.id],
  );

  const handleMsaTabChange = useCallback(
    (tab: MsaViewerTab) => {
      if (tab === "dotplot") {
        navigate({
          to: "/view/$fileId/dotplot",
          params: { fileId: file.id },
          replace: true,
        });
      } else if (tab === "heatmap") {
        navigate({
          to: "/view/$fileId/heatmap",
          params: { fileId: file.id },
          replace: true,
        });
      }
    },
    [navigate, file.id],
  );

  if (file.id !== fileId) {
    return null;
  }

  if (file.type === "GnomuxSequence") {
    return (
      <SequenceViewer
        sequenceId={file.content as string}
        tab="sequence"
        onTabChange={handleSequenceTabChange}
      />
    );
  }

  if (file.type === "GnomuxAlignment") {
    return (
      <MsaViewer file={file} tab="alignment" onTabChange={handleMsaTabChange} />
    );
  }

  if (file.type === "GnomuxSequenceCollection") {
    return <CollectionViewer file={file} sequences={sequences} />;
  }

  if (file.type === "GnomuxTree") {
    return <TreeViewer file={file} />;
  }

  return <div className="p-4 text-gray-500">Unknown file type.</div>;
}
